import { useDeleteIntegration } from "@/api/useIntegrations"
import { Alert, Divider, Flex, Tag, Text } from "@/atoms"
import { useLogger } from "@/hooks/useLogger"
import { DisconnectOutlined } from "@/icons"
import { DeleteConfirmModal, useConfirmModal } from "@/molecules/ConfirmModal"
import { Protected } from "@/molecules/Protected"
import {
  ConfigItem,
  FooterConfigItem
} from "@/pages/IntegrationsPage/components/molecules/ConfigItem"
import { FormPackageRegistry } from "@/pages/IntegrationsPage/components/molecules/FormPackageRegistry"
import { TestPackageRegistryButton } from "@/pages/IntegrationsPage/components/molecules/TestPackageRegistryButton"
import { useIntegration } from "@/pages/IntegrationsPage/hooks/useIntegration"
import { usePackageRegistryAPI } from "@/pages/IntegrationsPage/hooks/usePackageRegistryAPI"
import { INTEGRATIONS_WRITE_PERMISSION } from "@/pages/IntegrationsPage/IntegrationsPage.constants"

import styles from "./PackageRegistryConfigurationPage.module.scss"

import type { RegistryConnectionStatus } from "@/api/useIntegrations.types"
import type { PackageRegistryConfigurationItemProps } from "."

const STATUS_COLOR: Record<RegistryConnectionStatus, string | undefined> = {
  untested: undefined,
  connected: "success",
  authentication_failed: "error",
  unreachable: "warning",
  bad_url: "error"
}

const FAILED_STATUSES: RegistryConnectionStatus[] = [
  "authentication_failed",
  "unreachable",
  "bad_url"
]

export const PackageRegistryConfigurationItem = (
  props: PackageRegistryConfigurationItemProps
) => {
  const { entry, vendor } = props
  const [_, { onRemove: _onRemove, ...actions }] = useIntegration({ vendor })
  const { t } = actions
  const registryApi = usePackageRegistryAPI({ entry, ...actions })
  const remover = useDeleteIntegration({ vendor })
  const { confirm } = useConfirmModal()
  const { EVENTS } = useLogger()
  const prefix = "integrations.packageRegistry"
  const values = { id: entry.id, name: entry.endpoint }

  const onDisconnect = () =>
    confirm((attr) =>
      DeleteConfirmModal({
        name: entry.endpoint,
        entityName: vendor,
        okText: t("general.disconnect"),
        content: t(`${prefix}.disconnect.content`),
        ANALYTIC_EVENT:
          EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.PACKAGE_REGISTRY.REMOVED,
        ANALYTIC_TAGS: {
          package_managers: entry.packageManagers
        },
        onOk: () => remover.mutateAsync(entry.id),
        ...attr
      })
    )

  const packageManagers = entry.packageManagers
    .map((pm) => t(`${prefix}.form.packageManagers.options.${pm}`))
    .join(", ")

  return (
    <ConfigItem
      values={values}
      Aside={
        <Flex gap={4}>
          {entry.migrated && <Tag>{t(`${prefix}.configuration.migrated`)}</Tag>}
          <Tag color={STATUS_COLOR[entry.lastTestStatus]}>
            {t(`${prefix}.status.${entry.lastTestStatus}`)}
          </Tag>
        </Flex>
      }
      Description={
        <Flex gap={8} align="center" wrap>
          <Text type="secondary">{packageManagers}</Text>
          {entry.authMethod && (
            <>
              <Divider type="vertical" className={styles.separator} />
              <Text type="secondary">
                {t(`${prefix}.form.authMethod.options.${entry.authMethod}`)}
              </Text>
            </>
          )}
          {entry.migrated && (
            <>
              <Divider type="vertical" className={styles.separator} />
              <Text type="secondary">
                {t(`${prefix}.configuration.migratedNote`)}
              </Text>
            </>
          )}
        </Flex>
      }
    >
      {/* ConfigItem pads only a form that is its first child; the warning sits above it. */}
      <div className={styles.body}>
        {FAILED_STATUSES.includes(entry.lastTestStatus) && (
          <Alert
            showIcon
            type="warning"
            message={t(`${prefix}.configuration.testFailedWarning`)}
          />
        )}
        <FormPackageRegistry
          formId={registryApi.formId}
          entry={entry}
          onFinish={registryApi.onFinish}
          {...actions}
        />
      </div>
      <FooterConfigItem
        formId={registryApi.formId}
        isDirty={actions.isDirty}
        isPending={registryApi.isPending || remover.isPending}
        isTesting={registryApi.isTesting}
        permissions={INTEGRATIONS_WRITE_PERMISSION}
        removeTKey="general.disconnect"
        RemoveIcon={DisconnectOutlined}
        onRemove={onDisconnect}
        onReset={actions.onReset}
      >
        <Protected shouldDisable permission={INTEGRATIONS_WRITE_PERMISSION}>
          <TestPackageRegistryButton
            size="small"
            form={actions.form}
            t={t}
            registryApi={registryApi}
          />
        </Protected>
      </FooterConfigItem>
    </ConfigItem>
  )
}
